import { useSeoMeta } from '@unhead/react';
import { useEffect, useRef, useState } from 'react';
import {
  Terminal,
  ExternalLink,
  ArrowRight,
  ChevronDown,
  User,
  Server,
  Code2,
  Wrench,
  Rocket,
  RadioTower,
  Menu,
  X,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { SiteFooter } from '@/components/SiteFooter';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */
const GithubIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Nori SVG Mascot                                                    */
/* ------------------------------------------------------------------ */
const NoriLogo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <svg
    viewBox="0 0 20 16"
    shapeRendering="crispEdges"
    aria-label="Nori the ostrich"
    className={className}
  >
    <g fill="#7B68EE">
      <rect x="8" y="0" width="2" height="1"/>
      <rect x="7" y="1" width="1" height="2"/>
      <rect x="10" y="1" width="1" height="2"/>
      <rect x="6" y="3" width="2" height="1"/>
      <rect x="10" y="3" width="2" height="1"/>
      <rect x="4" y="4" width="10" height="1"/>
      <rect x="3" y="5" width="1" height="3"/>
      <rect x="14" y="5" width="1" height="3"/>
      <rect x="7" y="6" width="2" height="1"/>
      <rect x="4" y="8" width="2" height="1"/>
      <rect x="12" y="8" width="2" height="1"/>
      <rect x="6" y="9" width="6" height="1"/>
      <rect x="6" y="10" width="1" height="1"/>
      <rect x="11" y="10" width="1" height="1"/>
      <rect x="5" y="11" width="2" height="1"/>
      <rect x="11" y="11" width="2" height="1"/>
      <rect x="5" y="12" width="1" height="2"/>
      <rect x="12" y="12" width="1" height="2"/>
      <rect x="4" y="14" width="3" height="1"/>
      <rect x="9" y="14" width="4" height="1"/>
      <rect x="3" y="15" width="1" height="1"/>
      <rect x="6" y="15" width="1" height="1"/>
      <rect x="9" y="15" width="1" height="1"/>
      <rect x="12" y="15" width="1" height="1"/>
    </g>
    <g fill="#C4BBFF">
      <rect x="8" y="1" width="2" height="2"/>
      <rect x="8" y="3" width="2" height="1"/>
      <rect x="4" y="5" width="10" height="1"/>
      <rect x="4" y="6" width="3" height="1"/>
      <rect x="9" y="6" width="5" height="1"/>
      <rect x="4" y="7" width="10" height="1"/>
      <rect x="6" y="8" width="6" height="1"/>
      <rect x="7" y="10" width="4" height="1"/>
      <rect x="7" y="11" width="4" height="1"/>
      <rect x="6" y="12" width="6" height="1"/>
      <rect x="6" y="13" width="1" height="1"/>
      <rect x="9" y="13" width="3" height="1"/>
    </g>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Navigation                                                         */
/* ------------------------------------------------------------------ */
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'About', href: '/' },
    { label: 'Stack', href: '/stack' },
    { label: 'Get Started', href: '/get-started' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-background/80 backdrop-blur-xl border-b border-border/50'
          : 'bg-transparent'
      }`}
    >
      <div className="container grid grid-cols-[1fr_1fr] lg:grid-cols-[1fr_auto_1fr] items-center h-16">
        <div className="flex items-center">
          <a href="/" className="flex items-center gap-2.5 group shrink-0">
            <NoriLogo className="w-7 h-7 transition-transform group-hover:scale-110 shrink-0" />
            <span className="font-semibold text-lg tracking-tight whitespace-nowrap">
              nostr-station
            </span>
          </a>
        </div>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className={`text-sm transition-colors whitespace-nowrap ${
                link.href === '/stack'
                  ? 'text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center justify-end gap-3">
          <a
            href="https://github.com/jared-logan/nostr-station"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex"
          >
            <button className="inline-flex items-center justify-center rounded-full h-9 w-9 hover:bg-accent hover:text-accent-foreground transition-colors">
              <GithubIcon className="w-5 h-5" />
            </button>
          </a>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden inline-flex items-center justify-center rounded-full h-9 w-9 hover:bg-accent hover:text-accent-foreground transition-colors"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden border-t border-border/50">
          <nav className="container py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className={`text-sm transition-colors px-3 py-2 rounded-lg hover:bg-accent/50 ${
                  link.href === '/stack'
                    ? 'text-foreground font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="https://github.com/jared-logan/nostr-station"
              target="_blank"
              rel="noopener noreferrer"
              className="sm:hidden text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-accent/50 flex items-center gap-2"
            >
              <GithubIcon className="w-4 h-4" />
              GitHub
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

/* ------------------------------------------------------------------ */
/*  Hero Section                                                       */
/* ------------------------------------------------------------------ */
const HeroSection = () => {
  return (
    <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-16">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,hsl(265_85%_60%/0.15),transparent)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_600px_at_50%_50%,hsl(265_70%_55%/0.08),transparent)]" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(hsl(265 85% 60%) 1px, transparent 1px), linear-gradient(90deg, hsl(265 85% 60%) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />

      <div className="container relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Eyebrow */}
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            Stack
          </span>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
            What powers <span className="text-gradient">nostr-station</span>
          </h1>

          {/* Description */}
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Every piece is open source. Here's what's under the hood, who built it, and how it fits together.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  01 — Who it's for                                                  */
/* ------------------------------------------------------------------ */
const personas = [
  {
    icon: User,
    name: 'The Nostr Native',
    tagline: 'You want to use Nostr fully — on your own terms.',
    color: 'hsl(265 85% 60%)',
    gets: [
      'Your own Ditto client, no third-party relay required',
      'Encrypted mail and DMs that live on your own relay',
      'Browse nsites locally before they go public',
      'Mesh networking to reach your station from anywhere',
    ],
    scenario:
      'You wake up, open the dashboard at localhost:3000 and click into the #client panel — your own Ditto, your own relay. You post a note that publishes locally first, send an encrypted nostr-mail to a friend, and browse an nsite a builder sent you over the weekend. All from infra you control.',
  },
  {
    icon: Server,
    name: 'The Sovereign Host',
    tagline: 'You run infrastructure for yourself, your family, and your community.',
    color: 'hsl(280 85% 60%)',
    gets: [
      'A private relay shared across trusted users',
      'A local Blossom server for media that never leaves your network',
      'nvpn so family can connect from anywhere',
      'Bring-your-own AI for shared assistants',
    ],
    scenario:
      'The station runs on a NUC in your closet. Your partner posts photos that stay on your hardware. Your kid asks a local AI for homework help over the family VPN. Your friends DM you on a relay none of you have to trust anyone else to keep online.',
  },
  {
    icon: Code2,
    name: 'The Builder / Vibe Coder',
    tagline: 'You build apps on Nostr and want a complete local environment.',
    color: 'hsl(295 85% 60%)',
    gets: [
      'Local relay + Blossom for fast iteration with zero rate limits',
      'ngit + Amber for git-over-Nostr with cryptographic identity',
      'Claude Code, OpenCode, and a long list of AI providers wired in',
      'Nori for project-aware chat-driven edits in the dashboard',
      'Stacks Integration (`stacks mkstack`) for Bitcoin-secured app logic',
      'nsyte to publish your built app to the network',
    ],
    scenario:
      'You scaffold a new project with `stacks mkstack`, iterate against your local relay, sign commits with Amber, chat with Nori in the dashboard for inline edits, drop into Claude Code in the terminal for heavier lifting, and ship the static frontend as an nsite — without ever hitting a public service.',
  },
];

const WhoItsForSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="who" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,hsl(265_85%_60%/0.06),transparent)]" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            01 — Who it's for
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Do one, or <span className="text-gradient">do it all</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {personas.map((persona, index) => (
            <div
              key={persona.name}
              className={`group relative p-6 sm:p-8 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 hover:glow-purple ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                style={{ backgroundColor: `${persona.color}15` }}
              >
                <persona.icon className="w-6 h-6" style={{ color: persona.color }} />
              </div>
              <h3 className="text-lg font-semibold mb-1">{persona.name}</h3>
              <p className="text-sm text-muted-foreground mb-4">{persona.tagline}</p>

              <div className="mb-4">
                <span className="text-xs font-medium text-primary uppercase tracking-wider">What you get</span>
                <ul className="mt-2 space-y-2">
                  {persona.gets.map((item) => (
                    <li key={item} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-primary mt-1.5 flex-shrink-0 w-1 h-1 rounded-full bg-current" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed border-t border-border/30 pt-4">
                {persona.scenario}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  02 — The stack                                                     */
/* ------------------------------------------------------------------ */
const useNostrComponents = [
  {
    name: 'Self-hosted Client (Ditto)',
    description: 'A bundled Ditto SPA served locally as the #client panel of your dashboard.',
    sources: [
      { label: 'about page', url: 'https://about.ditto.pub/' },
      { label: 'client', url: 'https://ditto.pub' },
      { label: 'self-hosting docs', url: 'https://about.ditto.pub/self-hosting' },
    ],
    protocols: 'NIP-01, NIP-07, NIP-17, NIP-44, NIP-65',
    role: 'Bundled from upstream CI artifacts at build time, served from /ditto/*. Default upstream relay wss://relay.ditto.pub.',
  },
  {
    name: 'nostr-mail',
    description: 'Encrypted email over Nostr. NIP-17 gift-wrap hides sender and recipient metadata; RFC-2822 wire format inside sealed DMs.',
    sources: [
      { label: 'our own implementation', url: null },
      { label: 'wire protocol inspired by nostrmail.org', url: 'https://nostrmail.org' },
    ],
    protocols: 'NIP-17 (kinds 13 seal / 14 DM / 15 file), NIP-44 (encryption), NIP-59 (gift wrap, kind 1059), NIP-32 (labels, kind 1985), NIP-78 (settings, kind 30078), inbox relays (kind 10050), and kind 1301 for the RFC-2822 wire format.',
    role: 'Mail that lives on relays. Replaces SMTP for the people you trust enough to share a relay with.',
  },
  {
    name: 'nsite Browser',
    description: 'Verified, sandboxed browser for Nostr-hosted static sites.',
    href: '/nsite',
    sources: [
      { label: 'in-repo (src/lib/nsite-resolver.ts, src/lib/routes/nsite.ts, src/web/app.js)', url: null },
    ],
    protocols: 'nsite events (kind 34128 / 35128 / 15128), Blossom blob fetches, NIP-98 auth',
    role: 'Fetches manifests from relays, SHA256-verifies every blob against the signed manifest, renders each site in a unique *.nsite.localhost origin with strict CSP. See the Nsite page for the full security model.',
  },
  {
    name: 'nsyte',
    description: 'CLI for publishing nsites to Nostr relays and Blossom servers.',
    sources: [
      { label: 'nsyte.run (pinned upstream)', url: 'https://nsyte.run' },
    ],
    protocols: 'nsite events kind 34128 / 35128 / 15128',
    role: 'Build, sign, and publish static sites. Bundled so you can ship from the same terminal as the rest of your station.',
  },
  {
    name: 'Mesh VPN (nvpn)',
    description: 'Private mesh networking for remote access to your station.',
    sources: [
      { label: 'github.com/mmalmi/nostr-vpn — pinned v0.3.12', url: 'https://github.com/mmalmi/nostr-vpn' },
    ],
    protocols: 'not a Nostr protocol — Rust binary tunnel.',
    role: 'Reach your hardware from anywhere without exposing it to the public internet.',
  },
];

const buildOnNostrComponents = [
  {
    name: 'Local Relay',
    description: 'A custom in-process Nostr relay that runs in the same process tree as the station.',
    sources: [
      { label: 'in-repo (src/relay/), SQLite-backed via better-sqlite3', url: null },
    ],
    protocols: 'NIP-01, NIP-11, NIP-42, NIP-98',
    role: '~550 LoC of pure JS. No external relay daemon, no separate config file, no rate limits to fight.',
  },
  {
    name: 'Local Blossom Server',
    description: 'Lightweight in-process blob storage.',
    sources: [
      { label: 'in-repo (src/blossom/)', url: null },
    ],
    protocols: 'Blossom BUD-01, BUD-02, BUD-03; NIP-98 auth',
    role: 'Stores media and nsite assets locally. Serves blobs over HTTP on the same port as the dashboard.',
  },
  {
    name: 'Client (Ditto)',
    description: 'Embedded Nostr social client powered by upstream Ditto.',
    href: '/client',
    sources: [
      { label: 'upstream (gitlab.com/soapbox-pub/ditto), bundled and served from /ditto/ inside the dashboard', url: 'https://gitlab.com/soapbox-pub/ditto' },
    ],
    protocols: 'NIP-46 signing via Amber, configurable client relays (separate from project dev/prod env), kind-0 profile, kind-3 contacts, kind-1 posts, kind-6 reposts, kind-7 reactions, kind-16767 theme sync',
    role: 'The user-facing social surface of your station. Feeds, posting, notifications, profile management — without leaving the dashboard.',
  },
  {
    name: 'ngit',
    description: 'Native git workflows over Nostr.',
    sources: [
      { label: 'github.com/DanConwayDev/ngit-cli — pinned v2.4.3', url: 'https://github.com/DanConwayDev/ngit-cli' },
    ],
    protocols: 'NIP-34 — repo announcements (kind 30617), proposals (kind 1617), status updates (kinds 1630–1633), issues (kind 1621)',
    role: 'Version control where the repo, the patches, and the discussion all live on Nostr.',
  },
  {
    name: 'Amber',
    description: 'Remote signer for your Nostr identity.',
    sources: [
      { label: 'github.com/greenart7c3/Amber', url: 'https://github.com/greenart7c3/Amber' },
    ],
    protocols: 'NIP-46 (Nostr Connect)',
    role: 'Pair on first run; signs commits and events from your phone without exposing your private key to the station.',
  },
  {
    name: 'Nori',
    description: 'Chat-driven AI dev assistant built into the dashboard.',
    href: '/nori',
    sources: [
      { label: 'in-repo (src/web/app.js, src/lib/legacy-chat.ts, src/lib/editor.ts)', url: null },
    ],
    protocols: 'NIP-46 (signing via Amber), routes to any configured AI provider (Anthropic, OpenAI, Ollama, LM Studio, Groq, OpenRouter, OpenCode Zen, PayPerQ, Routstr)',
    role: 'Project-aware chat that reads README + custom context, can run edits, commits labeled with reasoning, keys stored in OS keychain.',
  },
  {
    name: 'Embedded Terminal',
    description: 'Live-streaming xterm.js sessions inside the dashboard.',
    sources: [
      { label: 'in-repo (src/lib/terminal.ts, src/lib/routes/terminal.ts)', url: null },
    ],
    protocols: 'not a Nostr protocol — shell session bridge',
    role: 'Run any shell command, git, npm, make, or AI agents (Claude Code, OpenCode) without leaving the dashboard. Backs the AI provider integrations.',
  },
  {
    name: 'Encrypted Keychain',
    description: 'OS-native storage for AI provider keys and bunker credentials.',
    sources: [
      { label: 'in-repo', url: null },
    ],
    protocols: 'macOS Keychain, GNOME Keyring, AES-256-GCM fallback',
    role: 'AI API keys and the silent re-auth bunker client never live in plaintext config files. Encrypted at ~/.nostr-station/bunker-client.json on systems without an OS keyring.',
  },
  {
    name: 'AI Providers',
    description: 'Bring-your-own AI agents and models.',
    sources: [
      { label: 'Claude Code', url: 'https://claude.ai/install.sh' },
      { label: 'OpenCode', url: 'https://github.com/sst/opencode' },
    ],
    sourceLabel: 'Agents',
    protocols: 'Anthropic, OpenAI, OpenRouter, OpenCode Zen, PayPerQ, Routstr, Ollama, LM Studio',
    protocolsLabel: 'Providers',
    role: 'Wired into the station terminal and workflows. Local models (Ollama, LM Studio) keep prompts on your hardware.',
  },
  {
    name: 'Stacks Integration',
    description: 'Scaffold and ship Bitcoin-secured Nostr apps.',
    sources: [
      { label: '@getstacks/stacks (mkstack scaffolder) at gitlab.com/soapbox-pub/mkstack, with the "Dork" agent built in', url: 'https://gitlab.com/soapbox-pub/mkstack' },
    ],
    protocols: 'Stacks / Clarity smart-contract layer (not a Nostr protocol).',
    role: 'Commands like `stacks mkstack`, `stacks agent`, `stacks configure` let you bootstrap a Stacks-backed Nostr app from one terminal.',
  },
];

const StackCard = ({
  component,
  index,
  visible,
}: {
  component: typeof useNostrComponents[0];
  index: number;
  visible: boolean;
}) => (
  <div
    className={`group relative p-6 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 hover:glow-purple ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    style={{ transitionDelay: `${index * 80}ms` }}
  >
    {component.href ? (
      <a href={component.href} className="group/link inline-block">
        <h3 className="text-lg font-semibold mb-2 inline-flex items-center gap-1.5 group-hover/link:text-primary transition-colors">
          {component.name}
          <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover/link:opacity-100 transition-opacity" />
        </h3>
      </a>
    ) : (
      <h3 className="text-lg font-semibold mb-2">{component.name}</h3>
    )}
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      {component.description}
    </p>

    <div className="space-y-3">
      <div>
        <span className="text-xs font-medium text-primary uppercase tracking-wider">
          {component.sourceLabel || 'Source'}
        </span>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
          {component.sources.map((source) =>
            source.url ? (
              <a
                key={source.label}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
              >
                {source.label}
                <ExternalLink className="w-3 h-3" />
              </a>
            ) : (
              <span key={source.label} className="text-sm text-muted-foreground">
                {source.label}
              </span>
            )
          )}
        </div>
      </div>

      <div>
        <span className="text-xs font-medium text-primary uppercase tracking-wider">
          {component.protocolsLabel || 'Protocols / NIPs'}
        </span>
        <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
          {component.protocols}
        </p>
      </div>

      <div>
        <span className="text-xs font-medium text-primary uppercase tracking-wider">Role</span>
        <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
          {component.role}
        </p>
      </div>
    </div>
  </div>
);

const TheStackSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="stack" ref={sectionRef} className="py-24 sm:py-32 relative">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            02 — The stack
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Components and <span className="text-gradient">protocols</span>
          </h2>
        </div>

        {/* Use Nostr group */}
        <div className="mb-12">
          <span className="text-primary text-xs font-medium tracking-wider uppercase mb-4 block">
            Use Nostr
          </span>
          <div className="grid sm:grid-cols-2 gap-6">
            {useNostrComponents.map((component, index) => (
              <StackCard key={component.name} component={component} index={index} visible={visible} />
            ))}
          </div>
        </div>

        {/* Build on Nostr group */}
        <div>
          <span className="text-primary text-xs font-medium tracking-wider uppercase mb-4 block">
            Build on Nostr
          </span>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {buildOnNostrComponents.map((component, index) => (
              <StackCard key={component.name} component={component} index={index} visible={visible} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  03 — Protocols & kinds at a glance                                 */
/* ------------------------------------------------------------------ */
const protocols = [
  { spec: 'NIP-01', what: 'Core protocol: events, filters, relays', usedBy: 'Relay, every component' },
  { spec: 'NIP-07', what: 'Browser extension signing (window.nostr)', usedBy: 'Ditto' },
  { spec: 'NIP-11', what: 'Relay information document', usedBy: 'Relay' },
  { spec: 'NIP-17', what: 'Private DMs (kind 14 / file kind 15, sealed in 13, gift-wrapped in 1059)', usedBy: 'nostr-mail, Ditto DMs' },
  { spec: 'NIP-32', what: 'Labels (kind 1985)', usedBy: 'nostr-mail folders and tags' },
  { spec: 'NIP-34', what: 'Git over Nostr (kinds 30617, 1617, 1621, 1630–1633)', usedBy: 'ngit' },
  { spec: 'NIP-42', what: 'Client authentication to relays', usedBy: 'Relay' },
  { spec: 'NIP-44', what: 'Versioned encryption', usedBy: 'nostr-mail, DMs' },
  { spec: 'NIP-46', what: 'Nostr Connect / remote signing', usedBy: 'Amber' },
  { spec: 'NIP-59', what: 'Gift wrap (kind 1059)', usedBy: 'nostr-mail' },
  { spec: 'NIP-65', what: 'Relay list metadata', usedBy: 'Ditto' },
  { spec: 'NIP-78', what: 'Application-specific data (kind 30078)', usedBy: 'nostr-mail settings' },
  { spec: 'NIP-98', what: 'HTTP auth', usedBy: 'Relay, Blossom' },
  { spec: 'Blossom BUD-01 / 02 / 03', what: 'Blob storage', usedBy: 'Local Blossom Server' },
  { spec: 'kind 10050', what: 'Inbox relays', usedBy: 'nostr-mail routing' },
  { spec: 'kind 34128 / 35128 / 15128', what: 'nsite manifests (v1 legacy 34128, v2 root 15128, v2 named 35128) and blob references', usedBy: 'nsite Browser, nsyte' },
  { spec: 'kind 1301', what: 'RFC-2822 wire format used by nostr-mail. Note: kind 1301 also appears in NIP-101e (workout records). nostr-mail uses 1301 inside its own NIP-17 envelope, so the collision is contextual rather than direct — but if the ecosystem standardizes around a different number, we\'ll follow.', usedBy: 'nostr-mail' },
];

const ProtocolsSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="protocols" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,hsl(265_85%_60%/0.06),transparent)]" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            03 — Protocols & kinds at a glance
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Reference <span className="text-gradient">table</span>
          </h2>
        </div>

        <div className={`max-w-4xl mx-auto transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs">
                      Spec
                    </th>
                    <th className="text-left px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs">
                      What it does
                    </th>
                    <th className="text-left px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs">
                      Used by
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {protocols.map((protocol, index) => (
                    <tr
                      key={protocol.spec}
                      className={`border-b border-border/30 hover:bg-primary/5 transition-colors ${index === protocols.length - 1 ? 'border-b-0' : ''}`}
                    >
                      <td className="px-6 py-4 font-mono text-foreground whitespace-nowrap">
                        {protocol.spec}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground min-w-[280px]">
                        {protocol.what}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground whitespace-nowrap">
                        {protocol.usedBy}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  04 — Coming soon                                                   */
/* ------------------------------------------------------------------ */
const ComingSoonSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="coming-soon" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="container">
        <div className={`max-w-3xl mx-auto text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            04 — Coming soon
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            On the <span className="text-gradient">roadmap</span>
          </h2>
        </div>

        <div className={`max-w-3xl mx-auto transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="relative rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-6 sm:p-8">
            <div className="absolute -top-3 left-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                <Wrench className="w-3.5 h-3.5" />
                Coming Soon
              </span>
            </div>
            <div className="grid gap-4 pt-2">
              <div className="flex items-start gap-3">
                <Rocket className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Deploy to nsite.</strong> One-click publish of your built apps via nsyte.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Terminal className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Titan-style packaging.</strong> Offline-first app distribution, inspired by the Titan browser project.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <RadioTower className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Meshtastic ↔ Nostr.</strong> Bridge <a href="https://meshtastic.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Meshtastic</a> mesh radio networks to Nostr relays for off-grid sync.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Profile Card                                                       */
/* ------------------------------------------------------------------ */
const profileCredits = [
  {
    pubkey: '266815e0c9210dfa324c6cba3573b14bee49da4209a9456f9484e5106cd408a5',
    dittoUrl: 'https://ditto.pub/hzrd149.com',
    contribution: 'Blossom and adjacent work',
  },
  {
    pubkey: '7579076d9aff0a4cfdefa7e2045f2486c7e5d8bc63bfc6b45397233e1bbfcb19',
    dittoUrl: 'https://ditto.pub/npub1w4uswmv6lu9yel005l3qgheysmr7tk9uvwluddznju3nuxalevvs2d0jr5',
    contribution: 'Amber',
  },
  {
    pubkey: 'a008def15796fba9a0d6fab04e8fd57089285d9fd505da5a83fe8aad57a3564d',
    dittoUrl: 'https://ditto.pub/danconwaydev.com',
    contribution: 'ngit',
  },
  {
    pubkey: '4523be58d395b1b196a9b8c82b038b6895cb02b683d0c253a955068dba1facd0',
    dittoUrl: 'https://ditto.pub/npub1g53mukxnjkcmr94fhryzkqutdz2ukq4ks0gvy5af25rgmwsl4ngq43drvk',
    contribution: 'nvpn',
  },
  {
    pubkey: '781a1527055f74c1f70230f10384609b34548f8ab6a0a6caa74025827f9fdae5',
    dittoUrl: 'https://ditto.pub/soapbox@ditto.pub',
    contribution: 'Ditto / Soapbox / mkstack',
  },
  {
    pubkey: '3bf0c63fcb93463407af97a5e5ee64fa883d107ef9e558472c4eb9aaaefa459d',
    dittoUrl: 'https://ditto.pub/fiatjaf.com',
    contribution: 'Nostr protocol',
  },
  {
    pubkey: 'b22b06b051fd5232966a9344a634d956c3dc33a7f5ecdcad9ed11ddc4120a7f2',
    dittoUrl: 'https://ditto.pub/npub1kg4sdvz3l4fr99n2jdz2vdxe2mpacva87hkdetv76ywacsfq5leqquw5te',
    contribution: 'nostrmail.org',
  },
  {
    pubkey: '0e29efc2a3eb6966403e8c61cb8bea6d78527af5e0565a2baa0f313eff142af2',
    dittoUrl: 'https://ditto.pub/josh@westernbtc.com',
    contribution: 'Titan Browser',
  },
];

const ProfileCard = ({ pubkey, dittoUrl, contribution }: typeof profileCredits[0]) => {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const isLoading = author.isLoading;
  const displayName = metadata?.display_name || metadata?.name || genUserName(pubkey);
  const handle = metadata?.nip05 || `${pubkey.slice(0, 8)}...${pubkey.slice(-4)}`;
  const picture = metadata?.picture;
  const about = metadata?.about;

  return (
    <a
      href={dittoUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative p-5 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 hover:glow-purple h-full flex flex-col"
    >
      <div className="flex items-start gap-4 flex-1 min-h-0">
        {isLoading ? (
          <Skeleton className="w-14 h-14 rounded-full flex-shrink-0" />
        ) : picture ? (
          <img
            src={picture}
            alt={displayName}
            className="w-14 h-14 rounded-full object-cover ring-1 ring-primary/30 flex-shrink-0"
            loading="lazy"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <NoriLogo className="w-8 h-8" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-full" />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-0.5">
                <h3 className="text-base font-semibold truncate">{displayName}</h3>
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </div>
              <p className="text-xs text-primary font-medium mb-1.5 truncate">{handle}</p>
              {about && (
                <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                  {about}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <div className="mt-auto pt-3 border-t border-border/30">
        <span className="text-xs text-muted-foreground">
          <span className="text-foreground font-medium">{contribution}</span>
        </span>
      </div>
    </a>
  );
};

/* ------------------------------------------------------------------ */
/*  05 — Inspirations & credits                                        */
/* ------------------------------------------------------------------ */
const CreditsSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="credits" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_100%,hsl(265_85%_60%/0.1),transparent)]" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            05 — Inspirations & credits
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-8">
            Standing on <span className="text-gradient">giants</span>
          </h2>
        </div>

        <div className={`max-w-5xl mx-auto transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-rows-4 lg:grid-cols-4 lg:grid-rows-2 gap-4 mb-10">
            {profileCredits.map((profile, index) => (
              <div
                key={profile.pubkey}
                className={`h-full ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${200 + index * 80}ms` }}
              >
                <ProfileCard {...profile} />
              </div>
            ))}
          </div>

          <div className="text-left p-6 sm:p-8 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm">
            <p className="text-muted-foreground leading-relaxed mb-4">
              Credit also goes to the Nostr protocol authors and the NIPs repo contributors, the nsyte authors, and everyone else whose work we build on top of.
            </p>
            <p className="text-foreground font-medium">
              nostr-station is glue, not invention — we owe everything to the people building the protocol.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */
const Stack = () => {
  useSeoMeta({
    title: 'Stack — nostr-station',
    description: 'Every piece is open source. Here\'s what\'s under the hood, who built it, and how it fits together.',
    ogTitle: 'Stack — nostr-station',
    ogDescription: 'Every piece is open source. Here\'s what\'s under the hood, who built it, and how it fits together.',
    twitterCard: 'summary_large_image',
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      <HeroSection />
      <WhoItsForSection />
      <TheStackSection />
      <ProtocolsSection />
      <ComingSoonSection />
      <CreditsSection />
      <SiteFooter />
    </div>
  );
};

export default Stack;
